import React, { useState, useEffect } from 'react'
import './CookieConsent.css'

interface CookieConsentProps {
  onAccept?: () => void
  onDecline?: () => void
}

// Module-level variable to store consent status in memory
let analyticsConsentStatus: 'accepted' | 'declined' | 'essential' | null = null

// Initialize from localStorage on module load (safely)
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    const storedConsent = localStorage.getItem('cookie_consent')
    if (storedConsent === 'accepted' || storedConsent === 'declined' || storedConsent === 'essential') {
      analyticsConsentStatus = storedConsent
    }
  }
} catch (e) {
  // localStorage not available (SSR, private browsing, etc.)
  console.warn('localStorage not accessible:', e)
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onDecline }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookie_consent')
    if (!consentStatus) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000)
    }
  }, [])

  const handleAccept = () => {
    analyticsConsentStatus = 'accepted'
    localStorage.setItem('cookie_consent', 'accepted')
    localStorage.setItem('cookie_consent_date', new Date().toISOString())
    setIsVisible(false)
    
    // Enable analytics and tracking
    if (onAccept) onAccept()
    
    // Load Microsoft Clarity if available
    loadMicrosoftClarity()
  }

  const handleDecline = () => {
    analyticsConsentStatus = 'declined'
    localStorage.setItem('cookie_consent', 'declined')
    localStorage.setItem('cookie_consent_date', new Date().toISOString())
    setIsVisible(false)
    
    if (onDecline) onDecline()
  }

  const handleAcceptEssential = () => {
    analyticsConsentStatus = 'essential'
    localStorage.setItem('cookie_consent', 'essential')
    localStorage.setItem('cookie_consent_date', new Date().toISOString())
    setIsVisible(false)
  }

  const loadMicrosoftClarity = () => {
    const clarityId = process.env.REACT_APP_CLARITY_PROJECT_ID
    if (clarityId && clarityId !== 'your_clarity_project_id') {
      // Validate clarityId format (alphanumeric only)
      if (!/^[a-zA-Z0-9]+$/.test(clarityId)) {
        console.error('Invalid Clarity Project ID format')
        return
      }
      
      // Load Microsoft Clarity script safely
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.innerHTML = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", ${JSON.stringify(clarityId)});
      `
      document.head.appendChild(script)
    }
  }

  if (!isVisible) return null

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <div className="cookie-consent-header">
            <h3>Cookie Settings</h3>
          </div>
          
          <div className="cookie-consent-body">
            <p>
              We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
              Your privacy is important to us.
            </p>
            
            {showDetails && (
              <div className="cookie-details">
                <div className="cookie-category">
                  <h4>Essential Cookies</h4>
                  <p>Required for basic site functionality. Cannot be disabled.</p>
                </div>
                
                <div className="cookie-category">
                  <h4>Analytics Cookies</h4>
                  <p>Help us understand how visitors interact with our website by collecting and reporting information anonymously.</p>
                  <ul>
                    <li>Microsoft Clarity - Session recordings and heatmaps</li>
                    <li>Supabase Analytics - User interaction tracking</li>
                  </ul>
                  <p><strong>Note:</strong> If you choose to add your email regardless of your cookie choice, we will always collect it to provide our service.</p>
                </div>
                
                <div className="cookie-category">
                  <h4>Marketing Cookies</h4>
                  <p>Used to track visitors across websites for marketing purposes.</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="cookie-consent-actions">
            <button 
              className="cookie-btn cookie-btn-link"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? 'Hide Details' : 'Cookie Details'}
            </button>
            
            <div className="cookie-btn-group">
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={handleAcceptEssential}
              >
                Essential Only
              </button>
              <button 
                className="cookie-btn cookie-btn-secondary"
                onClick={handleDecline}
              >
                Decline All
              </button>
              <button 
                className="cookie-btn cookie-btn-primary"
                onClick={handleAccept}
              >
                Accept All
              </button>
            </div>
          </div>
          
          <div className="cookie-consent-footer">
            <p>
              You can change your preferences anytime in our{' '}
              <a href="https://amplified-moon-ecd.notion.site/ARCHAAS-PRIVACY-POLICY-2498a0ce763c80cb8925f8a69df94e9f?pvs=143" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CookieConsent

// Utility functions for checking consent
export const getCookieConsent = (): string | null => {
  return analyticsConsentStatus
}

export const hasAnalyticsConsent = (): boolean => {
  return analyticsConsentStatus === 'accepted'
}

export const hasMarketingConsent = (): boolean => {
  return analyticsConsentStatus === 'accepted'
} 